import { styled } from "goober";
import { useEffect, useState } from "react";
import {
  desktopViewportFrameCss,
  useDesktopViewportScale,
} from "../hooks/useDesktopViewport";
import { executionContentPath } from "../lib/apiPaths";
import { apiUrl } from "../lib/tenantOrigin";
import { HtmlViewer } from "./HtmlViewer";
import { WavyLine } from "./WavyLine";

const Wrapper = styled("div")`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-primary);
  overflow: hidden;
`;

// White matches a normal browser canvas for pages that don't set body bg, so
// the rendered iframe area looks the same as opening the page in a tab.
const Thumbnail = styled(HtmlViewer)`
  ${desktopViewportFrameCss}
  position: absolute;
  top: 0;
  left: 0;
  border: none;
  display: block;
  background: white;
  pointer-events: none;
`;

type Props = {
  executionId: string;
  alt: string;
  baseRoute?: string;
};

export function PageThumbnail({ executionId, alt, baseRoute }: Props) {
  const externalApiUrl = apiUrl();
  const wrapperRef = useDesktopViewportScale<HTMLDivElement>();

  const [visible, setVisible] = useState(false);
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(wrapperRef.current);
    return () => obs.disconnect();
  }, [wrapperRef]);

  useEffect(() => {
    if (!visible) return;
    const ac = new AbortController();
    fetch(
      `${externalApiUrl}${executionContentPath(executionId, "output", { body: true })}`,
      { credentials: "include", signal: ac.signal }
    )
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((text) => setHtml(text))
      .catch((e) => {
        if (e?.name !== "AbortError") setFailed(true);
      });
    return () => ac.abort();
  }, [visible, executionId, externalApiUrl]);

  if (!visible || failed || !html) {
    return (
      <Wrapper ref={wrapperRef} aria-label={alt}>
        <WavyLine />
      </Wrapper>
    );
  }

  return (
    <Wrapper ref={wrapperRef} aria-label={alt}>
      <Thumbnail html={html} title={alt} baseRoute={baseRoute} />
    </Wrapper>
  );
}
